/**
 * Pole, do ktorého sa píše, nesmie ostať pod klávesnicou (T-28).
 *
 * Hlavnú prácu robí `interactive-widget=resizes-content` v `index.html`: na
 * Androide klávesnica stránku zmenší, takže `dvh` aj `fixed` vrstvy (formulár
 * ticketu, detail ticketu) sa zmestia nad ňu. Klávesnica sa ale vysúva AŽ PO
 * tom, čo pole dostalo fokus — prehliadač ho posúva do zorného poľa podľa
 * veľkosti spred otvorenia, a pole v spodnej časti vnoreného rolovacieho
 * panelu tak skončí tesne pod novým okrajom.
 *
 * Preto sa po každej zmene viditeľnej časti pozrie, či pole s fokusom ešte
 * vidno, a iba keď nie, posunie sa. Kontrola viditeľnosti je podstatná: na
 * iOS Safari si stránku posúva sám (`offsetTop`) a zbytočné `scrollIntoView`
 * by s ním bojovalo.
 */

type Env = {
  visualViewport: VisualViewport | null | undefined;
  document: Document;
  requestAnimationFrame: (callback: FrameRequestCallback) => number;
};

const isTextField = (element: Element | null): element is HTMLElement => {
  if (!element) {
    return false;
  }
  if (element instanceof HTMLTextAreaElement) {
    return !element.readOnly && !element.disabled;
  }
  if (element instanceof HTMLInputElement) {
    // Zaškrtávacie políčka, prepínače a tlačidlá klávesnicu neotvárajú.
    const nonText = ['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color'];
    return !nonText.includes(element.type) && !element.readOnly && !element.disabled;
  }
  return element instanceof HTMLElement && element.isContentEditable;
};

export const installKeepFocusedFieldVisible = ({
  visualViewport,
  document,
  requestAnimationFrame,
}: Env): (() => void) => {
  if (!visualViewport) {
    return () => {};
  }
  const vv = visualViewport;

  const check = () => {
    // Až v ďalšom snímku: pri udalosti `resize` ešte rozloženie stránky nemusí
    // zodpovedať novej výške a obdĺžnik poľa by bol ten starý.
    requestAnimationFrame(() => {
      const field = document.activeElement;
      if (!isTextField(field)) {
        return;
      }
      const rect = field.getBoundingClientRect();
      const visibleTop = vv.offsetTop;
      const visibleBottom = vv.offsetTop + vv.height;
      if (rect.bottom > visibleBottom || rect.top < visibleTop) {
        field.scrollIntoView({ block: 'nearest' });
      }
    });
  };

  vv.addEventListener('resize', check);
  return () => vv.removeEventListener('resize', check);
};
